import { Bell, Sun, Moon, Search, X, ClipboardList, MessageCircle, Wrench, Receipt, Users, Package, Loader2, ArrowRight } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useNotifications } from "@/lib/notifications";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { formatDistanceToNow } from "date-fns";
import api from "@/lib/api";

const kindIcon = { quote: ClipboardList, chat: MessageCircle, job: Wrench, booking: Wrench, invoice: Receipt };

const SEARCH_GROUPS = [
  { key: "quotes", label: "Quotes", icon: ClipboardList },
  { key: "jobs", label: "Jobs", icon: Wrench },
  { key: "customers", label: "Customers", icon: Users },
  { key: "products", label: "Products", icon: Package },
];

export default function AdminTopbar() {
  const { items, unread, markRead, markAll } = useNotifications();
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ quotes: [], jobs: [], customers: [], products: [] });
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function onClick(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, []);

  const runSearch = useCallback(async (value) => {
    if (!value.trim()) { setResults({ quotes: [], jobs: [], customers: [], products: [] }); return; }
    setLoading(true);
    try {
      const [quotes, jobs, customers, products] = await Promise.all([
        api.get("/admin/quotes", { params: { q: value } }).then(r => r.data).catch(() => []),
        api.get("/admin/jobs", { params: { q: value } }).then(r => r.data).catch(() => []),
        api.get("/admin/customers", { params: { q: value } }).then(r => r.data).catch(() => []),
        api.get("/products", { params: { q: value, limit: 5 } }).then(r => r.data).catch(() => []),
      ]);
      setResults({ quotes: quotes.slice(0, 4), jobs: jobs.slice(0, 4), customers: customers.slice(0, 4), products: products.slice(0, 4) });
    } finally { setLoading(false); }
  }, []);

  const onChange = (e) => {
    const value = e.target.value;
    setQ(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 300);
  };

  const totalResults = results.quotes.length + results.jobs.length + results.customers.length + results.products.length;

  const goto = (n) => {
    markRead(n.notification_id);
    if (n.kind === "quote") nav(`/admin/quotes?open=${n.ref?.quote_id || ""}`);
    else if (n.kind === "chat") nav(`/admin/chat?thread=${n.ref?.thread_id || ""}`);
    else if (n.kind === "job") nav("/admin/jobs");
    else if (n.kind === "invoice") nav("/admin/invoices");
  };

  const closeSearch = () => { setOpen(false); setQ(""); setResults({ quotes: [], jobs: [], customers: [], products: [] }); };

  return (
    <header className="sticky top-0 z-30 h-16 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="h-full flex items-center gap-3 px-6">
        <div ref={wrapRef} className="flex-1 max-w-md relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
          <Input
            value={q}
            onChange={onChange}
            onFocus={() => setOpen(true)}
            placeholder="Search quotes, jobs, customers, products…"
            data-testid="admin-topbar-search"
            className="pl-10 h-10"
          />
          {q && (
            <button type="button" onClick={closeSearch} className="absolute right-3 top-1/2 -translate-y-1/2 z-10" aria-label="Clear search">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}

          {open && q.trim() && (
            <div className="absolute left-0 top-full mt-2 w-[420px] max-w-[90vw] bg-popover text-popover-foreground border border-border rounded-xl shadow-2xl overflow-hidden z-50" data-testid="admin-search-panel">
              {loading ? (
                <div className="p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</div>
              ) : totalResults === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">No matches for &ldquo;{q}&rdquo;</div>
              ) : (
                <div className="max-h-[70vh] overflow-y-auto divide-y divide-border">
                  {SEARCH_GROUPS.map(g => {
                    const rows = results[g.key];
                    if (!rows.length) return null;
                    return (
                      <div key={g.key} className="p-3">
                        <div className="flex items-center gap-1.5 mb-1.5 text-eagle-gold px-1">
                          <g.icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">{g.label}</span>
                        </div>
                        {g.key === "quotes" && rows.map(r => (
                          <button key={r.quote_id} onClick={() => { nav(`/admin/quotes?open=${r.quote_id}`); closeSearch(); }} className="w-full text-left p-2 rounded-lg hover:bg-secondary transition-colors" data-testid={`admin-search-quote-${r.quote_id}`}>
                            <div className="text-sm font-medium truncate">{r.contact_name || "Unnamed"}</div>
                            <div className="text-xs text-muted-foreground truncate">{r.address}{r.suburb ? `, ${r.suburb}` : ""}</div>
                          </button>
                        ))}
                        {g.key === "jobs" && rows.map(r => (
                          <button key={r.job_id} onClick={() => { nav(`/admin/jobs?q=${encodeURIComponent(q)}`); closeSearch(); }} className="w-full text-left p-2 rounded-lg hover:bg-secondary transition-colors" data-testid={`admin-search-job-${r.job_id}`}>
                            <div className="text-sm font-medium truncate">{r.customer_name || "Unnamed"}</div>
                            <div className="text-xs text-muted-foreground truncate">{r.address}</div>
                          </button>
                        ))}
                        {g.key === "customers" && rows.map(r => (
                          <button key={r.user_id} onClick={() => { nav(`/admin/customers?q=${encodeURIComponent(q)}`); closeSearch(); }} className="w-full text-left p-2 rounded-lg hover:bg-secondary transition-colors" data-testid={`admin-search-customer-${r.user_id}`}>
                            <div className="text-sm font-medium truncate">{r.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                          </button>
                        ))}
                        {g.key === "products" && rows.map(r => (
                          <button key={r.product_id} onClick={() => { nav(`/admin/products?q=${encodeURIComponent(q)}`); closeSearch(); }} className="w-full text-left p-2 rounded-lg hover:bg-secondary transition-colors flex items-center gap-2" data-testid={`admin-search-product-${r.product_id}`}>
                            <div className="h-8 w-8 rounded bg-secondary overflow-hidden shrink-0">{r.images?.[0] && <img src={r.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                            <div className="min-w-0"><div className="text-sm font-medium truncate">{r.name}</div><div className="text-xs text-muted-foreground truncate">${(r.price || 0).toLocaleString()}</div></div>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="admin-theme-toggle"
            className="w-10 h-10 rounded-full border border-border hover:border-eagle-gold flex items-center justify-center transition"
          >
            {theme === "dark" ? <Moon className="h-4 w-4 text-eagle-gold" /> : <Sun className="h-4 w-4 text-eagle-gold" />}
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button className="relative w-10 h-10 rounded-full border border-border hover:border-eagle-gold flex items-center justify-center transition" data-testid="notification-bell">
                <Bell className="h-4 w-4" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-eagle-gold text-eagle-ink text-[10px] font-bold flex items-center justify-center">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <div className="font-display font-semibold">Notifications</div>
                {unread > 0 && <Button variant="ghost" size="sm" onClick={markAll} data-testid="notif-mark-all">Mark all read</Button>}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="p-8 text-sm text-center text-muted-foreground">You&apos;re all caught up.</div>
                ) : items.map(n => {
                  const Icon = kindIcon[n.kind] || Bell;
                  return (
                    <button
                      key={n.notification_id}
                      onClick={() => goto(n)}
                      data-testid={`notif-item-${n.notification_id}`}
                      className={`w-full text-left flex gap-3 p-3 border-b border-border hover:bg-secondary transition ${n.read ? "opacity-60" : ""}`}
                    >
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${n.read ? "bg-secondary" : "bg-eagle-gold/15"}`}>
                        <Icon className={`h-4 w-4 ${n.read ? "" : "text-eagle-gold"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{n.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{n.body}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</div>
                      </div>
                      {!n.read && <div className="h-2 w-2 rounded-full bg-eagle-gold shrink-0 mt-1" />}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-border">
            <div className="h-9 w-9 rounded-full bg-eagle-gold text-eagle-ink flex items-center justify-center text-sm font-bold">
              {user?.name?.[0] || "A"}
            </div>
            <div className="text-sm">
              <div className="font-medium leading-none">{user?.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Admin</div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}